// The calendar on a phone (screens/CALENDAR.md, "Phone: a day at a time"):
// a strip of the week's seven days, a dot on each that has something, and the
// day under it, swiped left or right. A tap on the strip goes to a day and a
// swipe on it moves a week, so Sunday is one tap away, never four swipes; the
// taps are also what a keyboard and a screen reader use.

import { useRef, useState, type ReactNode, type TouchEvent } from 'react';
import type { CalendarWeek } from '@fsp/domain';
import { Menu } from '../../kit/overlays';
import { Button, IconButton } from '../../kit/ui';
import { addDays, dateOf, dayStart, weekdayOf } from './days';
import { DayDetail } from './DayDetail';
import { dayLabelIn, type Entry } from './words';

const LETTER = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** A horizontal swipe: farther than a thumb's wobble, and more across than down. */
function useSwipe(onNext: () => void, onPrevious: () => void) {
  const start = useRef<{ x: number; y: number } | null>(null);
  return {
    onTouchStart: (e: TouchEvent) => {
      const t = e.touches[0];
      start.current = t === undefined ? null : { x: t.clientX, y: t.clientY };
    },
    onTouchEnd: (e: TouchEvent) => {
      const from = start.current;
      const t = e.changedTouches[0];
      start.current = null;
      if (from === null || t === undefined) return;
      const dx = t.clientX - from.x;
      const dy = t.clientY - from.y;
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      if (dx < 0) onNext();
      else onPrevious();
    },
  };
}

export function CalendarPhone({
  strip,
  day,
  today,
  zone,
  week,
  entriesOf,
  next,
  base,
  numbers,
  loading,
  leagueWeek,
  leagueWeekday,
  onPick,
  onLeagueWeek,
  zoneName,
  now,
  comingUp,
}: {
  strip: readonly string[];
  day: string;
  today: string;
  zone: string;
  week: CalendarWeek | null;
  entriesOf: (day: string) => readonly Entry[];
  next: Entry | null;
  base: string;
  numbers: boolean;
  loading: boolean;
  /** The strip starts on the day the league's week does. */
  leagueWeek: boolean;
  /** The day the league's week starts on; null when it has no weeks to start by, or starts on Sunday. */
  leagueWeekday: number | null;
  onPick: (day: string) => void;
  onLeagueWeek: (on: boolean) => void;
  /** "MDT": the zone every time here is in. */
  zoneName: string;
  now: number;
  /** Coming up, under today; null on any other day. */
  comingUp: ReactNode | null;
}) {
  // Which way the day came in, for its slide.
  const [came, setCame] = useState<'next' | 'previous' | null>(null);
  const go = (to: string) => {
    setCame(to > day ? 'next' : to < day ? 'previous' : null);
    onPick(to);
  };
  const daySwipe = useSwipe(
    () => go(addDays(day, 1)),
    () => go(addDays(day, -1)),
  );
  const stripSwipe = useSwipe(
    () => go(addDays(day, 7)),
    () => go(addDays(day, -7)),
  );
  const noon = dayStart(day, zone) + 12 * 3600 * 1000;
  const month = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: zone,
  }).format(new Date(noon));
  const entries = entriesOf(day);
  return (
    <div className="fsp-cal-phone">
      <div className="fsp-cal-phone-head">
        <span className="fsp-cal-phone-month">{month}</span>
        <span className="fsp-cal-zone fsp-muted">{zoneName}</span>
        {day !== today ? (
          <Button size="xs" variant="soft" onClick={() => go(today)}>
            Today
          </Button>
        ) : null}
        {leagueWeekday !== null ? (
          <Menu
            title="Week starts on"
            align="end"
            target={<IconButton icon="settings" label="Week starts on" variant="quiet" />}
            entries={[
              {
                kind: 'item',
                key: 'sunday',
                label: 'Sunday',
                current: !leagueWeek,
                onClick: () => onLeagueWeek(false),
              },
              {
                kind: 'item',
                key: 'league',
                label: `The league’s week, from ${DOW[leagueWeekday] ?? ''}`,
                current: leagueWeek,
                onClick: () => onLeagueWeek(true),
              },
            ]}
          />
        ) : null}
      </div>
      <div className="fsp-cal-strip" role="tablist" aria-label="Days" {...stripSwipe}>
        {strip.map((d) => {
          const on = entriesOf(d);
          return (
            <button
              key={d}
              type="button"
              role="tab"
              aria-selected={d === day}
              aria-label={`${dayLabelIn(dayStart(d, zone) + 12 * 3600 * 1000, zone)}${
                on.length === 0
                  ? ''
                  : `, ${String(on.length)} ${on.length === 1 ? 'thing' : 'things'} on it`
              }`}
              className="fsp-cal-strip-day"
              data-today={d === today || undefined}
              data-past={d < today || undefined}
              onClick={() => go(d)}
            >
              <span className="fsp-cal-strip-letter">{LETTER[weekdayOf(d)]}</span>
              <span className="fsp-cal-strip-date">{dateOf(d)}</span>
              <span
                className="fsp-cal-strip-dot"
                data-on={on.length > 0 || undefined}
                data-needs={on.some((e) => e.needsMe) || undefined}
              />
            </button>
          );
        })}
      </div>
      <div
        className="fsp-cal-phone-day"
        role="tabpanel"
        data-came={came ?? undefined}
        key={day}
        {...daySwipe}
      >
        <h2 className="fsp-cal-phone-title">{dayLabelIn(noon, zone)}</h2>
        <DayDetail
          dayName={DOW[weekdayOf(day)] ?? ''}
          label={dayLabelIn(noon, zone)}
          week={week}
          entries={entries}
          next={next}
          now={now}
          today={day === today}
          zone={zone}
          base={base}
          numbers={numbers}
          loading={loading}
        />
      </div>
      {comingUp}
    </div>
  );
}
